import type { OpenSource } from '@parcel/analytics';
import type { TableFilter } from '@parcel/filters';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from '@parcel/ui';
import { type Table as ReactTable, flexRender } from '@tanstack/react-table';
import { Download } from 'lucide-react';
import * as React from 'react';
import { reportEntityBrowseMountedRows } from '../../lib/entity-browse-observability.js';
import type { ProjectRow } from '../../lib/projects-display.js';
import {
  type AskWhereResponse,
  EMPTY_TABLE_FILTER,
  hasTableFilter,
} from '../../lib/table-filter.js';
import {
  DATA_TABLE_MULTI_LINE_ROW_HEIGHT,
  DATA_TABLE_STICKY_HEADER_TOP_CLASS,
  DataTableColumnsToggle,
  DataTableLoadStatus,
  DataTablePagination,
  DataTableRoot,
  DataTableSkeletonRows,
  DataTableToolbar,
  DataTableViewport,
  getColumnStyle,
  getPinnedColumnClassName,
  getPinnedHeaderClassName,
  useInfiniteTableVirtualizer,
} from '../data-table/data-table-layout.js';
import { useRegisterRecordNavigator } from '../entity-drawer/record-navigator.js';
import type { RecordMatch } from '../filter-bar/filter-menu.js';
import { WhereBar } from '../where-bar/where-bar.js';
import type { WhereOptions } from '../where-bar/where-menu.js';
import { PROJECTS_PINNED_COLUMNS } from './use-projects-table.js';

export interface OpenProjectDetail {
  id: string;
  seed: ProjectRow | null;
  source: OpenSource;
}

interface ProjectsDataTableProps {
  table: ReactTable<ProjectRow>;
  filterState: TableFilter;
  onFilterStateChange: (next: TableFilter) => void;
  options: WhereOptions;
  openProjectId: string | null;
  onOpenProject: (project: OpenProjectDetail | null) => void;
  isLoading?: boolean;
  isError?: boolean;
  onAsk?: (query: string) => Promise<AskWhereResponse>;
  selectionToolbar?: React.ReactNode;
  /** When rows are selected the toolbar becomes the selection bar; search and Columns hide. */
  selectionActive?: boolean;
  browseMode?: boolean;
  scopeKey?: string;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  isFetchNextPageError?: boolean;
  fetchNextPage?: () => Promise<unknown>;
  summaryTotal?: number | null;
  recordMatches?: readonly RecordMatch[];
  onRecordInputChange?: (text: string) => void;
  onSelectRecord?: (id: string) => void;
}

export function ProjectsDataTable({
  table,
  filterState,
  onFilterStateChange,
  options,
  openProjectId,
  onOpenProject,
  isLoading,
  isError = false,
  onAsk,
  selectionToolbar,
  selectionActive = false,
  browseMode = false,
  scopeKey = '',
  hasNextPage = false,
  isFetchingNextPage = false,
  isFetchNextPageError = false,
  fetchNextPage = async () => undefined,
  summaryTotal = null,
  recordMatches = [],
  onRecordInputChange,
  onSelectRecord,
}: ProjectsDataTableProps) {
  const rows = table.getRowModel().rows;
  const openIndex = openProjectId ? rows.findIndex((r) => r.original.id === openProjectId) : -1;
  const navigatorIds = React.useMemo(() => rows.map((row) => row.original.id), [rows]);
  useRegisterRecordNavigator({
    type: 'project',
    ids: navigatorIds,
    seedFor: (id) => rows.find((row) => row.original.id === id)?.original ?? null,
  });

  const openRecord = (id: string) => {
    if (onSelectRecord) {
      onSelectRecord(id);
      return;
    }
    const row = table.getCoreRowModel().rows.find((candidate) => candidate.original.id === id);
    if (row) onOpenProject({ id, seed: row.original, source: 'table' });
  };
  const filtersActive = hasTableFilter(filterState);
  // Server-side filters changed → snap back to page 1.
  // biome-ignore lint/correctness/useExhaustiveDependencies: filterState is a prop; object identity changes on each new filter selection
  React.useEffect(() => {
    if (!browseMode) table.setPageIndex(0);
  }, [browseMode, filterState, table]);

  const nextPageRequestRef = React.useRef<Promise<unknown> | null>(null);
  const requestNextPage = React.useCallback(() => {
    if (nextPageRequestRef.current) return nextPageRequestRef.current;
    const request = Promise.resolve().then(fetchNextPage);
    nextPageRequestRef.current = request;
    void request.then(
      () => {
        if (nextPageRequestRef.current === request) nextPageRequestRef.current = null;
      },
      () => {
        if (nextPageRequestRef.current === request) nextPageRequestRef.current = null;
      },
    );
    return request;
  }, [fetchNextPage]);

  const { listRef, virtualItems, paddingTop, paddingBottom, scrollToTop } =
    useInfiniteTableVirtualizer({
      rowCount: browseMode ? rows.length : 0,
      hasNextPage: browseMode && hasNextPage,
      isFetchingNextPage,
      fetchNextPage: requestNextPage,
      estimateSize: DATA_TABLE_MULTI_LINE_ROW_HEIGHT,
    });
  const mountedRowCount = virtualItems.length;
  const lastMountedBucketRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (!browseMode) {
      lastMountedBucketRef.current = null;
      return;
    }
    if (lastMountedBucketRef.current === mountedRowCount) return;
    lastMountedBucketRef.current = mountedRowCount;
    reportEntityBrowseMountedRows('project', true, mountedRowCount);
  }, [browseMode, mountedRowCount]);
  const previousScopeKeyRef = React.useRef(scopeKey);
  React.useEffect(() => {
    if (!browseMode || previousScopeKeyRef.current === scopeKey) return;
    previousScopeKeyRef.current = scopeKey;
    scrollToTop();
    table.resetRowSelection();
    // Cursor browse clears rows while the new scope loads. Closing against that
    // empty array would drop an open drawer on every sort/refresh even when the
    // entity remains in the result set. Only close once we have rows and the
    // open project is absent from them.
    if (
      openProjectId &&
      rows.length > 0 &&
      !rows.some((row) => row.original.id === openProjectId)
    ) {
      onOpenProject(null);
    }
  }, [browseMode, onOpenProject, openProjectId, rows, scopeKey, scrollToTop, table]);

  const requestedDrawerEdgeRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const atLoadedEdge = openIndex >= 0 && openIndex === rows.length - 1;
    if (!browseMode || !atLoadedEdge || !hasNextPage || isFetchingNextPage || !openProjectId) {
      if (!atLoadedEdge) requestedDrawerEdgeRef.current = null;
      return;
    }
    const edgeKey = `${openProjectId}:${rows.length}`;
    if (requestedDrawerEdgeRef.current === edgeKey) return;
    requestedDrawerEdgeRef.current = edgeKey;
    void requestNextPage().catch(() => undefined);
  }, [
    browseMode,
    hasNextPage,
    isFetchingNextPage,
    openIndex,
    openProjectId,
    requestNextPage,
    rows.length,
  ]);

  const renderedRows = browseMode
    ? virtualItems.flatMap((virtualRow) => {
        const row = rows[virtualRow.index];
        return row ? [row] : [];
      })
    : rows;

  return (
    <DataTableRoot>
      <DataTableToolbar>
        {selectionActive ? (
          selectionToolbar
        ) : (
          <>
            <WhereBar
              dataset="project"
              filter={filterState}
              onFilterChange={onFilterStateChange}
              options={options}
              records={recordMatches}
              onSelectRecord={openRecord}
              onInputChange={onRecordInputChange}
              recentKey="projects"
              onAsk={onAsk}
              askExample="permitted projects in boston this year"
            />
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" size="icon" className="h-9 w-9">
                <Download className="h-4 w-4" />
                <span className="sr-only">Export projects</span>
              </Button>
              <DataTableColumnsToggle table={table} />
            </div>
          </>
        )}
      </DataTableToolbar>

      <DataTableViewport>
        <Table
          className="min-w-[1120px]"
          containerClassName="relative h-auto w-full overflow-visible"
        >
          <TableHeader
            className={cn('sticky z-20 bg-background', DATA_TABLE_STICKY_HEADER_TOP_CLASS)}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    style={getColumnStyle(PROJECTS_PINNED_COLUMNS, header.column)}
                    className={cn(
                      getPinnedHeaderClassName(PROJECTS_PINNED_COLUMNS, header.column.id),
                      header.column.id === 'select' && 'p-0 text-center',
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody ref={browseMode ? listRef : undefined}>
            {isLoading ? (
              <DataTableSkeletonRows
                table={table}
                pinnedColumns={PROJECTS_PINNED_COLUMNS}
                rowClassName="h-16"
              />
            ) : isError && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={table.getAllLeafColumns().length} className="h-24 text-center">
                  Unable to load projects.
                </TableCell>
              </TableRow>
            ) : rows.length ? (
              <>
                {browseMode && paddingTop > 0 ? (
                  <tr>
                    <td colSpan={table.getAllLeafColumns().length} style={{ height: paddingTop }} />
                  </tr>
                ) : null}
                {renderedRows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    data-active={openProjectId === row.original.id ? 'true' : undefined}
                    className="group/row h-16 cursor-pointer data-[active]:bg-muted/60"
                    onClick={() =>
                      onOpenProject({ id: row.original.id, seed: row.original, source: 'table' })
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={getColumnStyle(PROJECTS_PINNED_COLUMNS, cell.column)}
                        className={cn(
                          getPinnedColumnClassName(PROJECTS_PINNED_COLUMNS, cell.column.id),
                          cell.column.id === 'select' && 'p-0 text-center',
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {browseMode && paddingBottom > 0 ? (
                  <tr>
                    <td
                      colSpan={table.getAllLeafColumns().length}
                      style={{ height: paddingBottom }}
                    />
                  </tr>
                ) : null}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={table.getAllLeafColumns().length} className="h-24 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <span>No projects found.</span>
                    {filtersActive ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onFilterStateChange(EMPTY_TABLE_FILTER)}
                      >
                        Clear all filters
                      </Button>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </DataTableViewport>

      {browseMode && !(isError && rows.length === 0) ? (
        <DataTableLoadStatus
          loaded={rows.length}
          total={summaryTotal}
          hasMore={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isFetchNextPageError={isFetchNextPageError}
          onRetry={() => void requestNextPage()}
        />
      ) : !browseMode ? (
        <DataTablePagination table={table} isLoading={isLoading} />
      ) : null}
    </DataTableRoot>
  );
}
